import { useEffect, useState } from "react";
import { Award, BadgeCheck, Briefcase, CreditCard, Globe2, Headset, Hospital, Landmark, Megaphone, QrCode, Rocket, Server, ShieldCheck, ShoppingCart, Timer, Users } from "lucide-react";
import { api, apiUrl } from "../lib/api";

const INDUSTRIES = ["Healthcare RCM", "IT Services", "BPO & KPO", "Banking & Fintech", "HR Teams", "Marketing Agencies", "E-commerce", "Consulting", "Startups", "Customer Support Centres", "Hospitals & Clinics", "Insurance"];
const SKILLS = ["AI at Work", "Cybersecurity", "Data Analytics", "Medical Coding", "Denial Management", "Project Management", "Digital Marketing", "HR Management", "Customer Service", "Business Communication", "Online Research"];
const INDUSTRY_ICONS = [[Hospital, "Hospitals & Clinics"], [Server, "IT Services"], [Landmark, "Banking & Finance"], [Headset, "BPO & Support"], [ShoppingCart, "E-commerce"], [Briefcase, "Consulting"], [ShieldCheck, "Insurance"], [Megaphone, "Marketing"], [Users, "HR & Staffing"], [Rocket, "Startups"]];

export const partnerLogoUrl = (p) => apiUrl(`/partners/${p.id}/logo?v=${p.logo_version}`);

const Partners = () => {
  const [items, setItems] = useState([]);
  const [broken, setBroken] = useState(new Set());
  useEffect(() => { api.get("/partners").then((r) => setItems(r.data.filter((p) => p.has_logo))).catch(() => {}); }, []);
  if (!items.length) return null;
  const loop = items.length < 6 ? [...items, ...items, ...items] : items;
  return (
    <div className="partners" data-testid="trust-partners">
      <div className="marquee-label">Our partner companies <span>and many more</span></div>
      <div className="marquee logo-marquee">
        <div className="marquee-track slow">{[...loop, ...loop].map((p, i) => (
          <div className="logo-chip" key={`${p.id}-${i}`} hidden={broken.has(p.id)} data-testid={i < items.length ? `partner-${p.id}` : undefined} aria-hidden={i >= items.length}>
            <img src={partnerLogoUrl(p)} alt={p.name} loading="lazy" onError={() => setBroken((b) => new Set(b).add(p.id))} />{p.label && <small>{p.label}</small>}
          </div>))}
        </div>
      </div>
    </div>
  );
};

const IndustryIcons = () => (
  <div className="industry-icons" data-testid="trust-industry-icons">{INDUSTRY_ICONS.map(([Icon, label]) => (
    <div className="industry-icon" key={label}><Icon size={22} /><span>{label}</span></div>))}
  </div>
);

const Marquee = ({ items, reverse, testId }) => (
  <div className="marquee" data-testid={testId}>
    <div className={reverse ? "marquee-track reverse" : "marquee-track"}>
      {[...items, ...items].map((t, i) => <span className="marquee-chip" key={i} aria-hidden={i >= items.length}>{t}</span>)}
    </div>
  </div>
);

export const TrustCarousel = () => (
  <section className="trust-band" data-testid="trust-band">
    <div className="trust-band-head">
      <div className="section-kicker"><Globe2 size={14} /> VERIFIABLE BY ANY EMPLOYER, WORLDWIDE</div>
      <h2>Built around the skills <span>top employers hire for.</span></h2>
      <p>Every AVAILITY certificate carries a QR code and ID that any recruiter, anywhere, can check instantly on our public verification page.</p>
      <div className="trust-point" data-testid="trust-questions-line"><BadgeCheck size={16} /> Questions built around current industry standards and real workplace scenarios.</div>
    </div>
    <Partners />
    <IndustryIcons />
    <div className="marquee-label">Relevant across industries</div>
    <Marquee items={INDUSTRIES} testId="trust-marquee-industries" />
    <div className="marquee-label">Skill areas we certify</div>
    <Marquee items={SKILLS} reverse testId="trust-marquee-skills" />
  </section>
);

const fmt = (n) => (n >= 1000 ? `${Math.floor(n / 100) / 10}k+` : n >= 10 ? `${Math.floor(n / 10) * 10}+` : String(n));

// Real learner/certificate totals appear once they reach this size; until then, true test facts fill the band.
const SOCIAL_PROOF_MIN = 500;
const FACTS = [["minutes", "30 min", "Online, timed test"], ["validity", "2 years", "Certificate validity"], ["qr", "Instant", "QR verification for employers"]];

export const LiveCounters = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/public/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const live = [["learners", s.learners, "Registered learners"], ["certificates", s.certificates, "Certificates issued"]]
    .filter(([, v]) => v >= SOCIAL_PROOF_MIN).map(([k, v, label]) => [k, fmt(v), label]);
  const items = [["certifications", s.certifications, "Certifications available"], ...live, ...FACTS].slice(0, 4);
  return (
    <section className="counters" data-testid="live-counters">{items.map(([k, v, label]) => (
      <div key={k} data-testid={`counter-${k}`}><strong>{v}</strong><span>{label}</span></div>))}
    </section>
  );
};

const STEPS = [[CreditCard, "Pick a certification", "Sign in and take the assessment free."], [Timer, "Take the timed test", "Answer the questions online; pass with 50% or more."], [QrCode, "Get verified", "Receive a QR-verified certificate any employer can check."]];

export const HowItWorks = () => (
  <section className="how-it-works" data-testid="how-it-works">
    <div className="section-kicker"><Award size={14} /> HOW IT WORKS</div>
    <div className="steps">{STEPS.map(([Icon, title, text], i) => (
      <div className="step" key={title} data-testid={`how-step-${i + 1}`}><span className="step-num">0{i + 1}</span><Icon size={22} /><h3>{title}</h3><p>{text}</p></div>))}
    </div>
  </section>
);
